import React, { useState, useEffect } from 'react';
import SmartAttendanceService from '../../services/SmartAttendanceService';
import StudentService from '../../services/StudentService';

// Helper to format attendance time to real Indian Standard Time (IST - 12-hour AM/PM)
const formatIndianTime = (rawTime) => {
  if (!rawTime || rawTime === '—' || rawTime === 'Recorded' || rawTime === 'null' || rawTime === 'undefined') {
    return rawTime || '—';
  }
  const str = String(rawTime).trim();
  if (!str) return '—';

  // If already formatted with AM/PM (e.g. "09:50:52 AM" or "09:50 AM"), return as is
  if (str.toUpperCase().includes('AM') || str.toUpperCase().includes('PM')) {
    return str;
  }

  // If it's a full ISO or date-time string
  if (str.includes('T') || (str.includes('-') && str.includes(':'))) {
    try {
      const d = new Date(str.endsWith('Z') || str.includes('+') ? str : (str + 'Z'));
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    } catch (_) {}
  }

  // If it's in "HH:mm:ss" or "HH:mm" 24h format from UTC server (e.g. "04:20:52", "11:41:14")
  const parts = str.split(':');
  if (parts.length >= 2) {
    try {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parts.length > 2 ? parseInt(parts[2], 10) : 0;
      if (!isNaN(h) && !isNaN(m)) {
        let totalMinutes = h * 60 + m + (5 * 60 + 30);
        let istSeconds = isNaN(s) ? 0 : s;
        let istHour = Math.floor(totalMinutes / 60) % 24;
        let istMin = totalMinutes % 60;

        const ampm = istHour >= 12 ? 'PM' : 'AM';
        const displayHour = istHour % 12 || 12;
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(displayHour)}:${pad(istMin)}:${pad(istSeconds)} ${ampm}`;
      }
    } catch (_) {}
  }

  return str;
};

const SmartAttendanceStudio = () => {
  const [activeTab, setActiveTab] = useState('review');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [academicYear, setAcademicYear] = useState('2026-2027');

  const [summary, setSummary] = useState({
    totalStudents: 0,
    presentCount: 0,
    absentCount: 0,
    lateCount: 0,
    halfDayCount: 0,
    absenceCandidates: 0,
    attendancePercentage: 0,
    qrScans: 0,
    barcodeScans: 0,
    rfidScans: 0,
    recentScans: []
  });

  // Class Review State
  const [selectedClass, setSelectedClass] = useState('1st Standard');
  const [selectedSection, setSelectedSection] = useState('Section A');
  const [selectedSession, setSelectedSession] = useState('MORNING');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [studentsRoster, setStudentsRoster] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sendAlertsOnFinalize, setSendAlertsOnFinalize] = useState(true);

  // Scanner Studio State
  const [scanInput, setScanInput] = useState('');
  const [scannerLocation, setScannerLocation] = useState('Main Gate A');
  const [lastScanResult, setLastScanResult] = useState(null);
  const [scanType, setScanType] = useState('QR');
  const [scanning, setScanning] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    schoolStartTime: '09:00',
    lateTime: '09:10',
    absentCutoffTime: '09:30',
    halfDayTime: '12:00',
    enableManual: true,
    enableQr: true,
    enableBarcode: true,
    enableRfid: true,
    enableFingerprint: false,
    enableFace: false,
    allowDuplicateScan: false,
    autoMarkAbsent: true,
    enableWhatsapp: true,
    enableSms: false,
    enableEmail: false,
    whatsappProvider: 'CUSTOM_GATEWAY',
    whatsappApiUrl: '',
    whatsappApiKey: '',
    whatsappSubUser: '',
    whatsappSenderNumber: '',
    whatsappAbsentTemplate: 'Dear Parent, your ward {{studentName}} (Class {{className}}-{{section}}, Roll No: {{rollNo}}) is marked ABSENT today ({{date}}). If this is an error, please contact the school office.',
    lowAttendanceThreshold: 75.0,
    warningThreshold: 80.0,
    criticalThreshold: 70.0
  });

  const [testPhone, setTestPhone] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadSummary();
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'review') {
      loadClassReview();
    }
  }, [selectedClass, selectedSection, selectedDate, selectedSession, academicYear, activeTab]);

  const loadSummary = async () => {
    try {
      const data = await SmartAttendanceService.getTodaySummary();
      if (data && data.success) {
        setSummary(data);
      }
    } catch (err) {
      console.error("Error loading today summary:", err);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await SmartAttendanceService.getSettings();
      if (data && data.id) {
        setSettings(data);
      }
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };

  const loadClassReview = async () => {
    setLoading(true);
    try {
      const data = await SmartAttendanceService.getClassLiveReview(
        selectedClass,
        selectedSection,
        selectedDate,
        selectedSession,
        academicYear
      );

      if (data && data.success && Array.isArray(data.students)) {
        setStudentsRoster(data.students);
      } else {
        // Fallback to fetch all students if API returns empty
        const allStudents = await StudentService.getAllStudents();
        const list = Array.isArray(allStudents) ? allStudents : (allStudents.students || []);
        const filtered = list.filter(s => {
          const cMatch = !selectedClass || s.className === selectedClass || (s.className && s.className.toLowerCase().includes(selectedClass.toLowerCase()));
          const sMatch = !selectedSection || !s.section || s.section.toLowerCase() === selectedSection.toLowerCase();
          return cMatch && sMatch;
        });

        const initialRoster = (filtered.length > 0 ? filtered : list.slice(0, 15)).map(s => ({
          studentId: s.studentId,
          name: s.name,
          rollNo: s.rollNo || s.studentId,
          className: s.className || selectedClass,
          section: s.section || selectedSection,
          status: 'ABSENCE_CANDIDATE',
          entryTime: null,
          source: 'NO_SCAN',
          isFinalized: false,
          correctionReason: null,
          photoUrl: s.photoUrl
        }));

        setStudentsRoster(initialRoster);
      }
    } catch (err) {
      console.error("Error loading class review:", err);
    } finally {
      setLoading(false);
    }
  };

  // Status Change handler in local roster state
  const handleRosterStatusChange = (studentId, newStatus, reason = null) => {
    setStudentsRoster(prev => prev.map(s => {
      if (s.studentId === studentId) {
        return {
          ...s,
          status: newStatus,
          correctionReason: reason !== null ? reason : s.correctionReason,
          source: (newStatus === 'PRESENT' && s.source === 'NO_SCAN') ? 'MANUAL' : s.source
        };
      }
      return s;
    }));
  };

  // Quick 1-Click "Forgot ID Card" action
  const handleForgotIdCard = async (student) => {
    try {
      const payload = {
        studentId: student.studentId,
        date: selectedDate,
        academicYear: academicYear,
        newStatus: 'PRESENT',
        reason: 'Forgot ID Card',
        performedBy: 'Class Teacher'
      };

      const res = await SmartAttendanceService.manualOverrideAttendance(payload);
      if (res && res.success) {
        handleRosterStatusChange(student.studentId, 'PRESENT', 'Forgot ID Card');
        loadSummary();
      } else {
        alert("Failed to update status: " + (res.error || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  // Mark all unverified as Present or Absent
  const markAllRoster = (status) => {
    setStudentsRoster(prev => prev.map(s => ({
      ...s,
      status: status,
      source: 'MANUAL',
      correctionReason: status === 'PRESENT' ? 'Bulk Teacher Verification' : 'Marked Absent'
    })));
  };

  // Submit and Finalize Class Attendance
  const handleFinalizeAttendance = async () => {
    setSubmitting(true);
    try {
      const records = studentsRoster.map(s => ({
        studentId: s.studentId,
        status: s.status === 'ABSENCE_CANDIDATE' ? 'ABSENT' : s.status,
        reason: s.correctionReason,
        source: s.source
      }));

      const payload = {
        className: selectedClass,
        section: selectedSection,
        session: selectedSession,
        academicYear: academicYear,
        sendAlerts: sendAlertsOnFinalize,
        teacherId: 101,
        records
      };

      const result = await SmartAttendanceService.finalizeClassAttendance(payload);
      if (result && result.success) {
        alert(`✅ ${result.message}`);
        loadClassReview();
        loadSummary();
      } else {
        alert("❌ Failed to finalize: " + (result.error || "Unknown error"));
      }
    } catch (err) {
      alert("❌ Submission error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Process live card scan
  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;

    setScanning(true);
    try {
      const result = await SmartAttendanceService.processScan(
        scanInput.trim(),
        scanType,
        'TERMINAL-01',
        null,
        scannerLocation,
        academicYear
      );
      setLastScanResult(result);
      setScanInput('');
      loadSummary();
      if (activeTab === 'review') loadClassReview();
    } catch (err) {
      setLastScanResult({ success: false, error: err.message });
    } finally {
      setScanning(false);
    }
  };

  // Save Settings
  const handleSaveSettings = async () => {
    try {
      const res = await SmartAttendanceService.saveSettings(settings);
      if (res && res.id) {
        alert("⚙️ Attendance Timings & Threshold Settings saved successfully!");
      }
    } catch (err) {
      alert("❌ Error saving settings: " + err.message);
    }
  };

  // Test WhatsApp Connection
  const handleTestWhatsApp = async () => {
    if (!testPhone.trim()) {
      alert("Please enter a valid mobile number to receive the test WhatsApp message.");
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await SmartAttendanceService.testWhatsAppConnection(
        testPhone.trim(),
        `🔔 [School ERP Test Alert] Connection successful! Your WhatsApp API Gateway and Sub-User (${settings.whatsappSubUser || 'Default'}) are properly configured.`
      );
      setTestResult(res);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTestLoading(false);
    }
  };

  // Counts for the active class review
  const classTotal = studentsRoster.length;
  const classPresent = studentsRoster.filter(s => s.status === 'PRESENT').length;
  const classLate = studentsRoster.filter(s => s.status === 'LATE').length;
  const classCandidates = studentsRoster.filter(s => s.status === 'ABSENCE_CANDIDATE' || s.status === 'NO_SCAN').length;
  const classAbsent = studentsRoster.filter(s => s.status === 'ABSENT').length;

  const filteredRoster = studentsRoster.filter(s => {
    const q = searchQuery.toLowerCase();
    return (s.name && s.name.toLowerCase().includes(q)) || (s.studentId && s.studentId.toLowerCase().includes(q));
  });

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Top Banner */}
      <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', padding: '24px 28px', color: '#ffffff', marginBottom: '24px', boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color: '#60a5fa', marginBottom: '8px', letterSpacing: '0.5px' }}>
              🛡️ SCHOOL ERP ATTENDANCE & VERIFICATION ENGINE
            </div>
            <h1 style={{ fontSize: '26px', fontWeight: '800', margin: '0 0 4px 0', letterSpacing: '-0.5px' }}>Smart Attendance Management Studio</h1>
            <p style={{ color: '#94a3b8', margin: 0, fontSize: '13px' }}>Raw Scan Ingestion • Arrival State Machine • Teacher Review • Automated Parent Absence Alerts</p>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600' }}>
              📅 Year: <strong style={{ color: '#60a5fa' }}>{academicYear}</strong>
            </div>
            <span style={{ padding: '6px 12px', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.4)', color: '#4ade80', borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>
              ⚡ Gate Scanners Active
            </span>
          </div>
        </div>

        {/* Real-time KPI Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginTop: '20px' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>TOTAL ENROLLED</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#ffffff' }}>{summary.totalStudents}</div>
          </div>
          <div style={{ background: 'rgba(34, 197, 94, 0.12)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(34, 197, 94, 0.25)' }}>
            <div style={{ fontSize: '11px', color: '#4ade80', fontWeight: '700' }}>PRESENT (ON-TIME)</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#22c55e' }}>{summary.presentCount} <span style={{ fontSize: '13px', fontWeight: '600' }}>({summary.attendancePercentage}%)</span></div>
          </div>
          <div style={{ background: 'rgba(245, 158, 11, 0.12)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
            <div style={{ fontSize: '11px', color: '#fbbf24', fontWeight: '700' }}>LATE ARRIVALS</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#f59e0b' }}>{summary.lateCount}</div>
          </div>
          <div style={{ background: 'rgba(239, 68, 68, 0.12)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.25)' }}>
            <div style={{ fontSize: '11px', color: '#f87171', fontWeight: '700' }}>FINALIZED ABSENT</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#ef4444' }}>{summary.absentCount}</div>
          </div>
          <div style={{ background: 'rgba(168, 85, 247, 0.12)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(168, 85, 247, 0.25)' }}>
            <div style={{ fontSize: '11px', color: '#c084fc', fontWeight: '700' }}>RAW SCANS INGESTED</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#a855f7' }}>{summary.qrScans + summary.barcodeScans + summary.rfidScans}</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '8px', background: '#ffffff', padding: '6px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', flexWrap: 'wrap' }}>
        {[
          { id: 'review', label: '📋 Period 1 Teacher Review & Live Roster', icon: '📋' },
          { id: 'scanner', label: '📷 Gate & ID Card Scanner Kiosk', icon: '📷' },
          { id: 'settings', label: '⚙️ Cutoff Timings & Alert Thresholds', icon: '⚙️' },
          { id: 'analytics', label: '📊 Principal Analytics & Audit Trail', icon: '📊' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: activeTab === tab.id ? '#2563eb' : 'transparent',
              color: activeTab === tab.id ? '#ffffff' : '#64748b'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: TEACHER DAILY ATTENDANCE & LIVE REVIEW STUDIO */}
      {activeTab === 'review' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          
          {/* Filter Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>CLASS</label>
                <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}>
                  {['1st Standard', '2nd Standard', '3rd Standard', '4th Standard', '5th Standard', '6th Standard', '7th Standard', '8th Standard', '9th Standard', '10th Standard'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>SECTION</label>
                <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}>
                  {['Section A', 'Section B', 'Section C'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>DATE</label>
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: '600' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>SESSION</label>
                <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}>
                  <option value="MORNING">Period 1 / Morning Roll Call</option>
                  <option value="AFTERNOON">Afternoon Session</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="🔍 Search name or ID..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', width: '200px' }}
              />
              <button onClick={() => markAllRoster('PRESENT')} style={{ padding: '7px 12px', background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                ✅ Mark All Present
              </button>
            </div>
          </div>

          {/* Quick Roster Status Summary Banner */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                Attendance – {selectedClass} ({selectedSection})
              </span>
              <div style={{ display: 'flex', gap: '10px', fontSize: '12px', fontWeight: '700' }}>
                <span style={{ color: '#64748b' }}>Total: {classTotal}</span>
                <span style={{ color: '#16a34a' }}>🟢 {classPresent} Present</span>
                <span style={{ color: '#d97706' }}>🟡 {classLate} Late</span>
                <span style={{ color: '#dc2626' }}>🔴 {classCandidates} Unscanned (Candidate)</span>
                {classAbsent > 0 && <span style={{ color: '#b91c1c' }}>⛔ {classAbsent} Finalized Absent</span>}
              </div>
            </div>

            <div style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Late Cutoff: <strong>{settings.lateTime}</strong></span>
              <span>•</span>
              <span>Absent Finalization: <strong>{settings.absentCutoffTime}</strong></span>
            </div>
          </div>

          {/* Student Roster List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>⏳ Loading live class scan evidence...</div>
          ) : filteredRoster.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>No students found in this class.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredRoster.map((student, idx) => {
                const isCandidate = student.status === 'ABSENCE_CANDIDATE' || student.status === 'NO_SCAN';
                const isPresent = student.status === 'PRESENT';
                const isLate = student.status === 'LATE';
                const isAbsent = student.status === 'ABSENT';

                return (
                  <div
                    key={student.studentId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 18px',
                      borderRadius: '10px',
                      border: isPresent ? '1px solid #bbf7d0' : isLate ? '1px solid #fef3c7' : isCandidate ? '1px solid #fecaca' : '1px solid #e2e8f0',
                      background: isPresent ? '#f0fdf4' : isLate ? '#fffbeb' : isCandidate ? '#fef2f2' : '#ffffff',
                      flexWrap: 'wrap',
                      gap: '12px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {/* Student Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '240px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', width: '20px' }}>{idx + 1}.</span>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: isPresent ? '#16a34a' : isLate ? '#d97706' : isCandidate ? '#dc2626' : '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '13px' }}>
                        {student.name ? student.name.charAt(0) : 'S'}
                      </div>
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '13.5px', color: '#0f172a' }}>{student.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          ID: <strong style={{ color: '#334155' }}>{student.studentId}</strong> • Roll: {student.rollNo}
                        </div>
                      </div>
                    </div>

                    {/* Scan Evidence & Timestamp */}
                    <div style={{ minWidth: '180px', fontSize: '12px' }}>
                      {isPresent && (
                        <div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', background: '#dcfce7', color: '#15803d', fontWeight: '800', fontSize: '11.5px' }}>
                            🟢 PRESENT
                          </span>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                            🕒 {formatIndianTime(student.entryTime) || '08:45 AM'} • {student.scannerLocation || student.source || 'Gate Scanner'}
                          </div>
                        </div>
                      )}

                      {isLate && (
                        <div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', background: '#fef3c7', color: '#b45309', fontWeight: '800', fontSize: '11.5px' }}>
                            🟡 LATE ARRIVAL
                          </span>
                          <div style={{ fontSize: '11px', color: '#b45309', marginTop: '2px' }}>
                            🕒 {formatIndianTime(student.entryTime) || '09:14 AM'} • {student.scannerLocation || student.source || 'Gate Scanner'}
                          </div>
                        </div>
                      )}

                      {isCandidate && (
                        <div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', background: '#fee2e2', color: '#b91c1c', fontWeight: '800', fontSize: '11.5px' }}>
                            🔴 ABSENCE CANDIDATE
                          </span>
                          <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px', fontWeight: '600' }}>
                            ⚠️ No Scan Recorded Yet
                          </div>
                        </div>
                      )}

                      {isAbsent && (
                        <div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b', fontWeight: '800', fontSize: '11.5px' }}>
                            ⛔ FINALIZED ABSENT
                          </span>
                        </div>
                      )}

                      {student.correctionReason && (
                        <div style={{ fontSize: '10.5px', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>
                          ℹ️ {student.correctionReason}
                        </div>
                      )}
                    </div>

                    {/* Fast Override Buttons */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {isCandidate && (
                        <button
                          type="button"
                          onClick={() => handleForgotIdCard(student)}
                          style={{
                            padding: '6px 12px',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          ⚡ Forgot ID Card
                        </button>
                      )}

                      {/* Status Selector Pills */}
                      {[
                        { val: 'PRESENT', label: 'P', color: '#16a34a' },
                        { val: 'LATE', label: 'L', color: '#d97706' },
                        { val: 'ABSENT', label: 'A', color: '#dc2626' },
                        { val: 'LEAVE', label: 'Leave', color: '#9333ea' }
                      ].map(opt => (
                        <button
                          key={opt.val}
                          type="button"
                          onClick={() => handleRosterStatusChange(student.studentId, opt.val)}
                          style={{
                            padding: '5px 8px',
                            borderRadius: '6px',
                            border: (student.status === opt.val || (isCandidate && opt.val === 'ABSENT')) ? `2px solid ${opt.color}` : '1px solid #cbd5e1',
                            background: student.status === opt.val ? opt.color : '#ffffff',
                            color: student.status === opt.val ? '#ffffff' : '#64748b',
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sticky Finalize Action Bar */}
          <div style={{ marginTop: '24px', padding: '18px 24px', background: '#0f172a', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#ffffff', flexWrap: 'wrap', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontWeight: '800', fontSize: '15px' }}>
                {classTotal} Students Ready for Finalization
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendAlertsOnFinalize}
                  onChange={e => setSendAlertsOnFinalize(e.target.checked)}
                  style={{ accentColor: '#2563eb', width: '14px', height: '14px' }}
                />
                <span>📲 Dispatch WhatsApp / SMS notifications to parents of absent students (Duplicate-Protected)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => markAllRoster('PRESENT')}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  fontWeight: '700',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Mark All Verified
              </button>

              <button
                type="button"
                onClick={handleFinalizeAttendance}
                disabled={submitting}
                style={{
                  padding: '10px 24px',
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '800',
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
                }}
              >
                {submitting ? '⏳ Finalizing...' : '🚀 Submit Attendance & Send Absent Alerts'}
              </button>
            </div>
          </div>

        </div>
      )}

      {/* TAB 2: GATE SCANNER KIOSK */}
      {activeTab === 'scanner' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px' }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>📷 Gate & ID Card Live Scanner Kiosk</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>LOCATION:</label>
                <select value={scannerLocation} onChange={e => setScannerLocation(e.target.value)} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: '600' }}>
                  <option value="Main Gate A">Main Gate A</option>
                  <option value="Main Gate B">Main Gate B</option>
                  <option value="School Bus 04">School Bus 04</option>
                  <option value="Classroom 5A">Classroom 5A</option>
                </select>
              </div>
            </div>

            {/* Input Mode Selector */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {['QR', 'BARCODE', 'RFID', 'FINGERPRINT'].map(type => (
                <button
                  key={type}
                  onClick={() => setScanType(type)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: scanType === type ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: scanType === type ? '#eff6ff' : '#ffffff',
                    color: scanType === type ? '#2563eb' : '#64748b',
                    fontWeight: '700',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  {type} Mode
                </button>
              ))}
            </div>

            {/* Simulated Bounding Scanner Frame */}
            <div style={{ background: '#090d16', borderRadius: '16px', padding: '36px', textAlign: 'center', color: '#ffffff' }}>
              <div style={{ width: '200px', height: '200px', margin: '0 auto 16px auto', border: '3px dashed #22c55e', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(34, 197, 94, 0.05)' }}>
                <span style={{ fontSize: '48px' }}>📷</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '12.5px', margin: '0 0 16px 0' }}>Hold ID card in front of camera or barcode gun</p>

              <form onSubmit={handleScanSubmit} style={{ display: 'flex', gap: '10px', maxWidth: '420px', margin: '0 auto' }}>
                <input
                  type="text"
                  placeholder="Scan or type Student ID (e.g. SCH-0030)..."
                  value={scanInput}
                  onChange={e => setScanInput(e.target.value)}
                  autoFocus
                  style={{ flex: 1, padding: '12px 14px', borderRadius: '8px', border: '1px solid #334155', background: '#1e293b', color: '#ffffff', fontSize: '13.5px' }}
                />
                <button type="submit" disabled={scanning} style={{ padding: '12px 20px', background: '#22c55e', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}>
                  {scanning ? '...' : 'Scan'}
                </button>
              </form>
            </div>
          </div>

          {/* Last Scan Result Card */}
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '800' }}>⚡ Scan Ingestion Result</h4>
            {lastScanResult ? (
              <div style={{ padding: '16px', borderRadius: '12px', background: lastScanResult.status === 'DUPLICATE' ? '#fffbeb' : lastScanResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${lastScanResult.status === 'DUPLICATE' ? '#fef3c7' : lastScanResult.success ? '#bbf7d0' : '#fca5a5'}` }}>
                <div style={{ fontSize: '13px', fontWeight: '800', color: lastScanResult.status === 'DUPLICATE' ? '#b45309' : lastScanResult.success ? '#16a34a' : '#dc2626', marginBottom: '8px' }}>
                  {lastScanResult.status === 'DUPLICATE' ? '⚠️ Already Recorded Today' : lastScanResult.success ? '✅ Arrival Verified & Logged' : '❌ Scan Error'}
                </div>
                {lastScanResult.studentName && (
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{lastScanResult.studentName}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>ID: {lastScanResult.studentId} | Class: {lastScanResult.className}-{lastScanResult.section}</div>
                    <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '700', marginTop: '6px' }}>
                      Arrival Time: {formatIndianTime(lastScanResult.entryTime || lastScanResult.markedTime)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                      Location: {lastScanResult.scannerLocation || scannerLocation}
                    </div>
                  </div>
                )}
                {lastScanResult.error && <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>{lastScanResult.error}</div>}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }}>Awaiting first scan...</div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: SETTINGS & CUTOFF TIMING CONFIG */}
      {activeTab === 'settings' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '800' }}>⚙️ School Attendance Timing & Threshold Policy</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>SCHOOL START TIME</label>
              <input type="time" value={settings.schoolStartTime} onChange={e => setSettings({...settings, schoolStartTime: e.target.value})} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              <span style={{ fontSize: '11px', color: '#64748b' }}>Morning gate opens</span>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>LATE CUTOFF TIME</label>
              <input type="time" value={settings.lateTime} onChange={e => setSettings({...settings, lateTime: e.target.value})} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              <span style={{ fontSize: '11px', color: '#64748b' }}>Scans after this time are marked LATE</span>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>ABSENT FINALIZATION CUTOFF</label>
              <input type="time" value={settings.absentCutoffTime || '09:30'} onChange={e => setSettings({...settings, absentCutoffTime: e.target.value})} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              <span style={{ fontSize: '11px', color: '#64748b' }}>Unscanned candidates finalized to ABSENT</span>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>LOW ATTENDANCE THRESHOLD (%)</label>
              <input type="number" min="50" max="95" value={settings.lowAttendanceThreshold || 75} onChange={e => setSettings({...settings, lowAttendanceThreshold: parseFloat(e.target.value)})} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              <span style={{ fontSize: '11px', color: '#64748b' }}>Triggers Smart Low-Attendance Alert</span>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700' }}>Parent Notification Channels</h4>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                <input type="checkbox" checked={settings.enableWhatsapp} onChange={e => setSettings({...settings, enableWhatsapp: e.target.checked})} style={{ accentColor: '#2563eb' }} />
                <span>WhatsApp Parent Alerts</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                <input type="checkbox" checked={settings.enableSms} onChange={e => setSettings({...settings, enableSms: e.target.checked})} style={{ accentColor: '#2563eb' }} />
                <span>SMS Alerts</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                <input type="checkbox" checked={settings.autoMarkAbsent} onChange={e => setSettings({...settings, autoMarkAbsent: e.target.checked})} style={{ accentColor: '#2563eb' }} />
                <span>Auto-Mark Unscanned Candidates as Absent after Cutoff</span>
              </label>
            </div>
          </div>

          {/* WHATSAPP API GATEWAY & SUB-USER CONFIGURATION */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '24px', marginTop: '24px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                  📱
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>WhatsApp API Gateway & Sub-User Integration</h4>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>Connect your WhatsApp API Gateway and allocate sub-users for direct parent absent alerts.</p>
                </div>
              </div>
              <span style={{ padding: '4px 10px', borderRadius: '20px', background: settings.enableWhatsapp ? '#dcfce7' : '#f1f5f9', color: settings.enableWhatsapp ? '#15803d' : '#64748b', fontSize: '11px', fontWeight: '800' }}>
                {settings.enableWhatsapp ? '🟢 WhatsApp Active' : '⚪ Disabled'}
              </span>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>GATEWAY PROVIDER</label>
                  <select
                    value={settings.whatsappProvider || 'CUSTOM_GATEWAY'}
                    onChange={e => setSettings({ ...settings, whatsappProvider: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}
                  >
                    <option value="CUSTOM_GATEWAY">Custom WhatsApp Gateway / Webhook</option>
                    <option value="ULTRAMSG">UltraMsg API Gateway</option>
                    <option value="WATI">WATI WhatsApp Business</option>
                    <option value="INTERAKT">Interakt API</option>
                    <option value="GENERIC_REST">Generic REST Gateway</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>WHATSAPP API ENDPOINT URL</label>
                  <input
                    type="text"
                    placeholder="https://api.yourgateway.com/send-message"
                    value={settings.whatsappApiUrl || ''}
                    onChange={e => setSettings({ ...settings, whatsappApiUrl: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b' }}>Endpoint for POST message requests</span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>API KEY / AUTH TOKEN</label>
                  <input
                    type="password"
                    placeholder="Enter API Key or Bearer Token..."
                    value={settings.whatsappApiKey || ''}
                    onChange={e => setSettings({ ...settings, whatsappApiKey: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b' }}>Authorization token for API calls</span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>SCHOOL SUB-USER / INSTANCE ID</label>
                  <input
                    type="text"
                    placeholder="e.g. subuser_sch02 or instance89201"
                    value={settings.whatsappSubUser || ''}
                    onChange={e => setSettings({ ...settings, whatsappSubUser: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b' }}>Assigned sub-user instance for this school</span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>SENDER PHONE / CHANNEL ID (OPTIONAL)</label>
                  <input
                    type="text"
                    placeholder="+919876543210"
                    value={settings.whatsappSenderNumber || ''}
                    onChange={e => setSettings({ ...settings, whatsappSenderNumber: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>
              </div>

              {/* Message Template Editor */}
              <div style={{ marginTop: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>PARENT ABSENT MESSAGE TEMPLATE</label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {['{{studentName}}', '{{className}}', '{{section}}', '{{rollNo}}', '{{date}}', '{{schoolName}}'].map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setSettings({ ...settings, whatsappAbsentTemplate: (settings.whatsappAbsentTemplate || '') + ' ' + tag })}
                        style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#ffffff', fontSize: '11px', fontWeight: '600', color: '#2563eb', cursor: 'pointer' }}
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  rows="3"
                  value={settings.whatsappAbsentTemplate || ''}
                  onChange={e => setSettings({ ...settings, whatsappAbsentTemplate: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontFamily: 'monospace' }}
                />
              </div>

              {/* Live Test WhatsApp API Connection */}
              <div style={{ marginTop: '16px', borderTop: '1px dashed #cbd5e1', paddingTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Enter Admin Mobile (e.g. 9876543210)..."
                    value={testPhone}
                    onChange={e => setTestPhone(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', width: '250px' }}
                  />
                  <button
                    type="button"
                    onClick={handleTestWhatsApp}
                    disabled={testLoading}
                    style={{ padding: '8px 16px', background: '#16a34a', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer' }}
                  >
                    {testLoading ? '⏳ Sending Test...' : '📲 Send Test WhatsApp Alert'}
                  </button>
                </div>

                {testResult && (
                  <div style={{ padding: '6px 14px', borderRadius: '8px', background: testResult.success ? '#dcfce7' : '#fee2e2', color: testResult.success ? '#15803d' : '#b91c1c', fontSize: '12px', fontWeight: '700' }}>
                    {testResult.success ? '✅ Test Message Dispatched Successfully!' : `❌ Failed: ${testResult.error || 'Check credentials'}`}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button onClick={handleSaveSettings} style={{ padding: '10px 24px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)' }}>
            💾 Save Timing, WhatsApp & Policy Settings
          </button>
        </div>
      )}

      {/* TAB 4: PRINCIPAL ANALYTICS & AUDIT */}
      {activeTab === 'analytics' && (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '800' }}>📊 Principal Live Analytics & Audit Trail</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>TODAY'S ATTENDANCE RATE</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#2563eb' }}>{summary.attendancePercentage}%</div>
            </div>
            <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>TOTAL PRESENT STUDENTS</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#16a34a' }}>{summary.presentCount}</div>
            </div>
            <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>UNSCANNED CANDIDATES</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#ef4444' }}>{summary.absenceCandidates}</div>
            </div>
          </div>

          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700' }}>Recent Ingested Scans (Immutable Scan Log)</h4>
          {summary.recentScans && summary.recentScans.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Student ID</th>
                    <th style={{ padding: '8px 12px' }}>Class</th>
                    <th style={{ padding: '8px 12px' }}>Status</th>
                    <th style={{ padding: '8px 12px' }}>Arrival Time</th>
                    <th style={{ padding: '8px 12px' }}>Source / Location</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentScans.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '8px 12px', fontWeight: '700' }}>{s.studentId}</td>
                      <td style={{ padding: '8px 12px' }}>{s.className}-{s.section}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ padding: '2px 6px', borderRadius: '4px', background: s.status === 'PRESENT' ? '#dcfce7' : '#fef3c7', color: s.status === 'PRESENT' ? '#15803d' : '#b45309', fontWeight: '700' }}>
                          {s.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px' }}>{s.time}</td>
                      <td style={{ padding: '8px 12px', color: '#64748b' }}>{s.source || s.scanType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '12px' }}>No raw scans recorded yet today.</div>
          )}
        </div>
      )}

    </div>
  );
};

export default SmartAttendanceStudio;
